/**
 * How a claim's state is drawn, and in what order the filters offer it.
 *
 * Here rather than in each screen because the desk's list, the customer's
 * cards and the detail header all render the same badge — and the one that
 * matters is `open`: drawn as a neutral tone it reads as "nothing to do",
 * when it is precisely the state that is waiting for a person.
 */

import type { BadgeTone, StatusTone } from "@hardauto/core-ui";

import type { NotePriority, NoteServiceCode, TicketReason, TicketStatus } from "./api";

export const STATUS_TONE: Record<TicketStatus, BadgeTone> = {
  // Waiting for somebody. A warning, because that is what it is.
  open: "warning",
  in_progress: "info",
  resolved: "success",
  refused: "danger",
};

/** The order the filters use: what needs attention first. */
export const STATUSES: readonly TicketStatus[] = ["open", "in_progress", "resolved", "refused"];

export const REASONS: readonly TicketReason[] = [
  "warranty",
  "wrong_reference",
  "damaged",
  "missing",
  "other",
];

export const SERVICES: readonly NoteServiceCode[] = ["sales", "logistics", "collections", "adv"];

export const PRIORITIES: readonly NotePriority[] = ["normal", "high", "urgent"];

export const PRIORITY_TONE: Record<NotePriority, BadgeTone> = {
  normal: "neutral",
  high: "warning",
  urgent: "danger",
};

/**
 * How one step of the progress line is drawn.
 *
 * The server sends `done` · `current` · `todo`; this maps them onto the dot's
 * tone. A `current` step is accented rather than neutral because it is the
 * answer to the only question a customer opens this page with: where is it.
 */
export function stepTone(state: string): StatusTone {
  if (state === "done") return "success";
  if (state === "current") return "accent";
  return "neutral";
}
